import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Flex,
  Group,
  Loader,
  Modal,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from "mantine-react-table";
import {
  IconCoins,
  IconPencil,
  IconSearch,
  IconTrash,
  IconUserOff,
  IconUserCheck,
  IconWalletOff,
} from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useDebouncedValue } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";

type UserRow = {
  id: string;
  email: string;
  name: string;
  designation: string;
  isActive: boolean;
  isVerified: boolean;
  createdAt: Date;
  creditBalance: number;
  company: { id: string; legalName: string; gtid: string; status: string } | null;
};

const PAGE_SIZE = 15;

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);
  const [editTarget, setEditTarget] = useState<UserRow | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    designation: "",
    email: "",
    phoneNumber: "",
  });
  const [deleting, setDeleting] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [creditTarget, setCreditTarget] = useState<UserRow | null>(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditReason, setCreditReason] = useState("");
  const [addingCredits, setAddingCredits] = useState(false);
  const [deductTarget, setDeductTarget] = useState<UserRow | null>(null);
  const [deductAmount, setDeductAmount] = useState("");
  const [deductReason, setDeductReason] = useState("");
  const [deductingCredits, setDeductingCredits] = useState(false);
  const [twoFactorOpen, setTwoFactorOpen] = useState(false);
  const [twoFactorOtp, setTwoFactorOtp] = useState("");
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const pendingTwoFactorActionRef = useRef<
    | null
    | { kind: "ADD" | "DEDUCT"; userId: string; credits: number; reason?: string }
    | { kind: "TOGGLE_ACTIVE"; userId: string; isActive: boolean; email: string }
    | {
      kind: "EDIT_USER";
      userId: string;
      name?: string;
      designation?: string;
      email?: string;
      phoneNumber?: string;
    }
    | { kind: "DELETE_USER"; userId: string; email: string }
  >(null);
  const [togglingActiveId, setTogglingActiveId] = useState<string | null>(null);

  const previousParamsRef = useRef<string>("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trpc.admin.listUsers.query({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedSearch.trim() || undefined,
      });
      setUsers(
        res.users.map((u) => ({
          ...u,
          createdAt: u.createdAt instanceof Date ? u.createdAt : new Date(u.createdAt as unknown as string),
        }))
      );
      setTotalRows(res.meta.total);
    } catch (err) {
      handleTrpcError(err);
      setUsers([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    const paramsString = JSON.stringify({
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      search: debouncedSearch,
    });
    if (paramsString === previousParamsRef.current) {
      return;
    }
    previousParamsRef.current = paramsString;
    void fetchUsers();
  }, [fetchUsers, pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
  }, [debouncedSearch]);

  const toggleUserActive = useCallback(async (row: UserRow, isActive: boolean) => {
    setTogglingActiveId(row.id);
    try {
      await trpc.admin.setUserActive.mutate({ userId: row.id, isActive });
      notifications.show({
        title: isActive ? "User enabled" : "User disabled",
        message: isActive
          ? `${row.email} can sign in again.`
          : `${row.email} cannot sign in until re-enabled.`,
        color: "green",
      });
      await fetchUsers();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("2FA_REQUIRED")) {
        pendingTwoFactorActionRef.current = { kind: "TOGGLE_ACTIVE", userId: row.id, isActive, email: row.email };
        setTwoFactorOtp("");
        setTwoFactorOpen(true);
      } else {
        handleTrpcError(err);
      }
    } finally {
      setTogglingActiveId(null);
    }
  }, [fetchUsers]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await trpc.user.delete.mutate({ userId: deleteTarget.id });
      notifications.show({
        title: "User removed",
        message: `${deleteTarget.email} and related data were deleted.`,
        color: "green",
      });
      setDeleteTarget(null);
      await fetchUsers();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("2FA_REQUIRED")) {
        pendingTwoFactorActionRef.current = { kind: "DELETE_USER", userId: deleteTarget.id, email: deleteTarget.email };
        setTwoFactorOtp("");
        setTwoFactorOpen(true);
      } else {
        handleTrpcError(err);
      }
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (row: UserRow) => {
    setEditTarget(row);
    setEditForm({
      name: row.name || "",
      designation: row.designation || "",
      email: row.email || "",
      phoneNumber: "",
    });
  };

  const saveUserEdit = async () => {
    if (!editTarget) return;
    setSavingEdit(true);
    try {
      await trpc.user.updateProfile.mutate({
        userId: editTarget.id,
        name: editForm.name.trim() || undefined,
        designation: editForm.designation.trim() || undefined,
        email: editForm.email.trim() || undefined,
        phoneNumber: editForm.phoneNumber.trim() || undefined,
      });
      notifications.show({
        title: "User updated",
        message: `${editForm.email.trim() || editTarget.email} details were updated.`,
        color: "green",
      });
      setEditTarget(null);
      await fetchUsers();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("2FA_REQUIRED")) {
        pendingTwoFactorActionRef.current = {
          kind: "EDIT_USER",
          userId: editTarget.id,
          name: editForm.name.trim() || undefined,
          designation: editForm.designation.trim() || undefined,
          email: editForm.email.trim() || undefined,
          phoneNumber: editForm.phoneNumber.trim() || undefined,
        };
        setTwoFactorOtp("");
        setTwoFactorOpen(true);
      } else {
        handleTrpcError(err);
      }
    } finally {
      setSavingEdit(false);
    }
  };

  const openCreditModal = (row: UserRow) => {
    setCreditTarget(row);
    setCreditAmount("");
    setCreditReason("");
  };

  const addCredits = async () => {
    if (!creditTarget) return;
    const parsedCredits = Number(creditAmount.trim());
    if (!Number.isInteger(parsedCredits) || parsedCredits <= 0) {
      notifications.show({
        title: "Invalid credit amount",
        message: "Enter a whole number between 1 and 100.",
        color: "red",
      });
      return;
    }
    if (parsedCredits > 100) {
      notifications.show({
        title: "Credit limit exceeded",
        message: "Maximum 100 credits allowed per operation.",
        color: "red",
      });
      return;
    }

    setAddingCredits(true);
    try {
      const result = await trpc.admin.addUserCredits.mutate({
        userId: creditTarget.id,
        credits: parsedCredits,
        reason: creditReason.trim() || undefined,
      });
      notifications.show({
        title: "Credits added",
        message: `${parsedCredits} credits added. New balance: ${result.newBalance} credits.`,
        color: "green",
      });
      setCreditTarget(null);
      await fetchUsers();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      // If admin has 2FA enabled, server will demand step-up verification.
      if (msg.includes("2FA_REQUIRED")) {
        pendingTwoFactorActionRef.current = {
          kind: "ADD",
          userId: creditTarget.id,
          credits: parsedCredits,
          reason: creditReason.trim() || undefined,
        };
        setTwoFactorOtp("");
        setTwoFactorOpen(true);
      } else {
        handleTrpcError(err);
      }
    } finally {
      setAddingCredits(false);
    }
  };

  const openDeductModal = (row: UserRow) => {
    setDeductTarget(row);
    setDeductAmount("");
    setDeductReason("");
  };

  const deductCredits = async () => {
    if (!deductTarget) return;
    const parsedCredits = Number(deductAmount.trim());
    if (!Number.isInteger(parsedCredits) || parsedCredits <= 0) {
      notifications.show({
        title: "Invalid credit amount",
        message: "Enter a whole number between 1 and 100.",
        color: "red",
      });
      return;
    }
    if (parsedCredits > 100) {
      notifications.show({
        title: "Credit limit exceeded",
        message: "Maximum 100 credits allowed per operation.",
        color: "red",
      });
      return;
    }

    setDeductingCredits(true);
    try {
      const result = await trpc.admin.deductUserCredits.mutate({
        userId: deductTarget.id,
        credits: parsedCredits,
        reason: deductReason.trim() || undefined,
      });
      notifications.show({
        title: "Credits deducted",
        message: `${parsedCredits} credits deducted. New balance: ${result.newBalance} credits.`,
        color: "green",
      });
      setDeductTarget(null);
      await fetchUsers();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("2FA_REQUIRED")) {
        pendingTwoFactorActionRef.current = {
          kind: "DEDUCT",
          userId: deductTarget.id,
          credits: parsedCredits,
          reason: deductReason.trim() || undefined,
        };
        setTwoFactorOtp("");
        setTwoFactorOpen(true);
      } else {
        handleTrpcError(err);
      }
    } finally {
      setDeductingCredits(false);
    }
  };

  const confirmTwoFactor = async () => {
    const pending = pendingTwoFactorActionRef.current;
    if (!pending) {
      setTwoFactorOpen(false);
      return;
    }
    const otp = twoFactorOtp.trim();
    if (!otp) {
      notifications.show({ title: "2FA required", message: "Enter your authenticator code.", color: "red" });
      return;
    }

    setTwoFactorBusy(true);
    try {
      if (pending.kind === "ADD") {
        const result = await trpc.admin.addUserCredits.mutate({
          userId: pending.userId,
          credits: pending.credits,
          reason: pending.reason,
          otp,
        });
        notifications.show({
          title: "Credits added",
          message: `${pending.credits} credits added. New balance: ${result.newBalance} credits.`,
          color: "green",
        });
        setCreditTarget(null);
      } else if (pending.kind === "DEDUCT") {
        const result = await trpc.admin.deductUserCredits.mutate({
          userId: pending.userId,
          credits: pending.credits,
          reason: pending.reason,
          otp,
        });
        notifications.show({
          title: "Credits deducted",
          message: `${pending.credits} credits deducted. New balance: ${result.newBalance} credits.`,
          color: "green",
        });
        setDeductTarget(null);
      } else if (pending.kind === "TOGGLE_ACTIVE") {
        await trpc.admin.setUserActive.mutate({
          userId: pending.userId,
          isActive: pending.isActive,
          otp,
        });
        notifications.show({
          title: pending.isActive ? "User enabled" : "User disabled",
          message: pending.isActive
            ? `${pending.email} can sign in again.`
            : `${pending.email} cannot sign in until re-enabled.`,
          color: "green",
        });
      } else if (pending.kind === "EDIT_USER") {
        await trpc.user.updateProfile.mutate({
          userId: pending.userId,
          name: pending.name,
          designation: pending.designation,
          email: pending.email,
          phoneNumber: pending.phoneNumber,
          otp,
        });
        notifications.show({
          title: "User updated",
          message: "User details were updated.",
          color: "green",
        });
        setEditTarget(null);
      } else if (pending.kind === "DELETE_USER") {
        await trpc.user.delete.mutate({ userId: pending.userId, otp });
        notifications.show({
          title: "User removed",
          message: `${pending.email} and related data were deleted.`,
          color: "green",
        });
        setDeleteTarget(null);
      }
      pendingTwoFactorActionRef.current = null;
      setTwoFactorOpen(false);
      await fetchUsers();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const columns = useMemo<MRT_ColumnDef<UserRow>[]>(
    () => [
      {
        accessorKey: "email",
        header: "Email",
      },
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        id: "company",
        header: "Company",
        Cell: ({ row }) => {
          const c = row.original.company;
          if (!c) return <Text c="dimmed" size="sm">—</Text>;
          return (
            <Text size="sm">
              {c.legalName}
              <Text span c="dimmed" size="xs" display="block">
                {c.gtid} · {c.status}
              </Text>
            </Text>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Joined",
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const d = cell.getValue<Date>();
          return <Text size="sm">{d.toLocaleDateString()}</Text>;
        },
      },
      {
        accessorKey: "creditBalance",
        header: "Credits",
        size: 90,
        Cell: ({ cell }) => {
          const balance = Number(cell.getValue<number>() || 0);
          return <Text size="sm">{balance}</Text>;
        },
      },
      {
        id: "status",
        header: "Status",
        size: 110,
        Cell: ({ row }) => {
          const active = row.original.isActive;
          return (
            <Badge size="sm" variant="light" color={active ? "green" : "gray"}>
              {active ? "Active" : "Disabled"}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "",
        size: 120,
        Cell: ({ row }) => {
          const r = row.original;
          const busy = togglingActiveId === r.id;
          return (
            <Group gap={4} wrap="nowrap" justify="flex-end">
              {r.isActive ? (
                <Tooltip label="Disable sign-in for this user">
                  <ActionIcon
                    variant="subtle"
                    color="orange"
                    loading={busy}
                    disabled={busy}
                    onClick={() => void toggleUserActive(r, false)}
                    aria-label="Deactivate user"
                  >
                    <IconUserOff size={18} />
                  </ActionIcon>
                </Tooltip>
              ) : (
                <Tooltip label="Allow this user to sign in again">
                  <ActionIcon
                    variant="subtle"
                    color="green"
                    loading={busy}
                    disabled={busy}
                    onClick={() => void toggleUserActive(r, true)}
                    aria-label="Activate user"
                  >
                    <IconUserCheck size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Tooltip label="Edit user details">
                <ActionIcon
                  variant="subtle"
                  color="blue"
                  disabled={busy}
                  onClick={() => openEditModal(r)}
                  aria-label="Edit user"
                >
                  <IconPencil size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Add wallet credits">
                <ActionIcon
                  variant="subtle"
                  color="teal"
                  disabled={busy || !r.company}
                  onClick={() => openCreditModal(r)}
                  aria-label="Add credits"
                >
                  <IconCoins size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Deduct wallet credits">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  disabled={busy || !r.company}
                  onClick={() => openDeductModal(r)}
                  aria-label="Deduct credits"
                >
                  <IconWalletOff size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Delete user and all related data">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  disabled={busy}
                  onClick={() => setDeleteTarget(r)}
                  aria-label="Delete user"
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          );
        },
      },
    ],
    [togglingActiveId, toggleUserActive]
  );

  return (
    <Flex direction="column" p="lg" gap="md" mih="100%">
      <div>
        <Group justify="space-between" align="center">
          <Title order={2}>Users</Title>
          <Button variant="light" onClick={() => navigate("/admin/credit-history")}>
            Credit history
          </Button>
        </Group>
        <Text c="dimmed" size="sm" mt={4}>
          Client accounts. Deleting a user removes their company, collaborations, ledger entries, uploaded letters, and
          other linked records.
        </Text>
      </div>

      <TextInput
        placeholder="Search by email, name, or company name"
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        maw={360}
      />

      <MantineReactTable
        columns={columns}
        data={users}
        state={{
          isLoading: loading,
          pagination,
        }}
        onPaginationChange={setPagination}
        manualPagination
        rowCount={totalRows}
        enableGlobalFilter={false}
        enableColumnFilters={false}
        mantineTableContainerProps={{ style: { minHeight: 400 } }}
      />

      {loading && users.length === 0 && (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      <Modal
        opened={!!editTarget}
        onClose={() => !savingEdit && setEditTarget(null)}
        title="Edit user"
        centered
      >
        {editTarget && (
          <>
            <TextInput
              label="Name"
              value={editForm.name}
              onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.currentTarget.value }))}
              mb="sm"
            />
            <TextInput
              label="Designation"
              value={editForm.designation}
              onChange={(e) => setEditForm((prev) => ({ ...prev, designation: e.currentTarget.value }))}
              mb="sm"
            />
            <TextInput
              label="Email"
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.currentTarget.value }))}
              mb="sm"
            />
            <TextInput
              label="Phone Number"
              placeholder="Optional"
              value={editForm.phoneNumber}
              onChange={(e) => setEditForm((prev) => ({ ...prev, phoneNumber: e.currentTarget.value }))}
            />
            <Flex justify="flex-end" gap="sm" mt="lg">
              <Button variant="default" onClick={() => setEditTarget(null)} disabled={savingEdit}>
                Cancel
              </Button>
              <Button onClick={() => void saveUserEdit()} loading={savingEdit}>
                Save changes
              </Button>
            </Flex>
          </>
        )}
      </Modal>

      <Modal
        opened={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete user"
        centered
      >
        {deleteTarget && (
          <>
            <Text size="sm">
              Permanently delete <strong>{deleteTarget.email}</strong>? This removes their company profile, interests,
              collaborations, wallet ledger, uploaded files, and cleans
              shortlist references. This cannot be undone.
            </Text>
            <Flex justify="flex-end" gap="sm" mt="lg">
              <Button variant="default" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </Button>
              <Button color="red" onClick={() => void confirmDelete()} loading={deleting}>
                Delete user
              </Button>
            </Flex>
          </>
        )}
      </Modal>

      <Modal
        opened={!!creditTarget}
        onClose={() => !addingCredits && setCreditTarget(null)}
        title="Add credits"
        centered
      >
        {creditTarget && (
          <>
            <Text size="sm" mb="sm">
              Add credits directly to <strong>{creditTarget.email}</strong>
              {creditTarget.company ? ` (${creditTarget.company.legalName})` : ""}.
            </Text>
            <TextInput
              label="Credits"
              placeholder="Enter whole number"
              type="number"
              min={1}
              max={100}
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.currentTarget.value)}
              mb="sm"
            />
            <TextInput
              label="Reason"
              placeholder="Optional note for audit log"
              value={creditReason}
              onChange={(e) => setCreditReason(e.currentTarget.value)}
            />
            <Flex justify="flex-end" gap="sm" mt="lg">
              <Button variant="default" onClick={() => setCreditTarget(null)} disabled={addingCredits}>
                Cancel
              </Button>
              <Button color="teal" onClick={() => void addCredits()} loading={addingCredits}>
                Add credits
              </Button>
            </Flex>
          </>
        )}
      </Modal>

      <Modal
        opened={!!deductTarget}
        onClose={() => !deductingCredits && setDeductTarget(null)}
        title="Deduct credits"
        centered
      >
        {deductTarget && (
          <>
            <Text size="sm" mb="sm">
              Deduct credits directly from <strong>{deductTarget.email}</strong>
              {deductTarget.company ? ` (${deductTarget.company.legalName})` : ""}.
            </Text>
            <TextInput
              label="Credits"
              placeholder="Enter whole number"
              type="number"
              min={1}
              max={100}
              value={deductAmount}
              onChange={(e) => setDeductAmount(e.currentTarget.value)}
              mb="sm"
            />
            <TextInput
              label="Reason"
              placeholder="Optional note for audit log"
              value={deductReason}
              onChange={(e) => setDeductReason(e.currentTarget.value)}
            />
            <Flex justify="flex-end" gap="sm" mt="lg">
              <Button variant="default" onClick={() => setDeductTarget(null)} disabled={deductingCredits}>
                Cancel
              </Button>
              <Button color="red" onClick={() => void deductCredits()} loading={deductingCredits}>
                Deduct credits
              </Button>
            </Flex>
          </>
        )}
      </Modal>

      <Modal
        opened={twoFactorOpen}
        onClose={() => !twoFactorBusy && setTwoFactorOpen(false)}
        title="Two-factor verification"
        centered
      >
        <Text size="sm" mb="sm">
          Enter the 6-digit code from your authenticator app to continue.
        </Text>
        <TextInput
          label="2FA code"
          placeholder="123456"
          value={twoFactorOtp}
          onChange={(e) => setTwoFactorOtp(e.currentTarget.value)}
          inputMode="numeric"
        />
        <Flex justify="flex-end" gap="sm" mt="lg">
          <Button variant="default" onClick={() => setTwoFactorOpen(false)} disabled={twoFactorBusy}>
            Cancel
          </Button>
          <Button onClick={() => void confirmTwoFactor()} loading={twoFactorBusy}>
            Verify & continue
          </Button>
        </Flex>
      </Modal>
    </Flex>
  );
}
